import {
  CalendarClock, Gauge, Hand, Inbox, LayoutGrid, Medal, MessageSquare, Repeat, ShoppingCart, Trophy, UserPlus, Webhook, Zap
} from 'lucide-react'
import { registeredModules } from '@/modules/registry'

/**
 * The trigger blocks of the palette: one block per family ("Membros"), and
 * the event ("entrou", "saiu"…) is picked inside the block's panel. Purely
 * the app's way of choosing: the graph still stores the event's own type
 * (`member.joined`), so saved workflows and the API contract are unchanged,
 * and an old workflow opens on the right block. A block is one thing (a
 * module or a subject): families of the same thing share it. Order here
 * is the order on screen (by potential use); a family not mapped here gets its own block after
 * these, so a new catalog family still shows up.
 *
 * A block of a space module (its families, people-management prefixes and
 * icon) is declared by the module (`workflow.trigger` in `src/modules`); this
 * file keeps only the subjects that are no module, and the screen order.
 */
/* Fixed and the same for everyone (David, 2026-10-26): what most communities tend to automate comes first. */
export const TRIGGER_BLOCKS = [
  'members', 'purchases', 'subscriptions', 'time',
  'events', 'courses', 'forms', 'manual', 'webhook',
  'certificates', 'badges', 'levels', 'surveys', 'community', 'messages', 'downloads', 'spaces', 'upvotes', 'proposals',
  'bookings', 'networking', 'attendance', 'reviews', 'oneOnOnes', 'goals', 'pdi', 'climate', 'recognition'
] as const

export type TriggerBlockKey = typeof TRIGGER_BLOCKS[number]

/* Subjects that are no space module. One block per thing, never folded by size: a certificate also comes from an event, so it is not a course. */
const SUBJECT_FAMILIES: Record<string, string> = {
  member: 'members', purchase: 'purchases', checkout: 'purchases', subscription: 'subscriptions', badge: 'badges',
  post: 'community', comment: 'community', reaction: 'community', space: 'spaces', message: 'messages'
}

/* People management (`hr`) is one catalog family over several modules: its events and date checks go to the module's block. */
const SUBJECT_HR_PREFIXES: [string, string][] = [['survey.round_', 'climate'], ['time.survey_', 'climate']]

const SUBJECT_ICONS: Record<string, typeof Zap> = {
  members: UserPlus, purchases: ShoppingCart, subscriptions: Repeat, badges: Medal, levels: Trophy, community: MessageSquare, messages: Inbox, spaces: LayoutGrid,
  climate: Gauge, time: CalendarClock, manual: Hand, webhook: Webhook
}

/* Read from the registry on first use, not at load: the registry imports screens that import this file. */
let moduleBlocks: { families: Record<string, string>; hrPrefixes: [string, string][]; icons: Record<string, typeof Zap> } | null = null
const blocks = () => {
  if (!moduleBlocks) {
    const families: Record<string, string> = { ...SUBJECT_FAMILIES }
    const hrPrefixes: [string, string][] = [...SUBJECT_HR_PREFIXES]
    const icons: Record<string, typeof Zap> = { ...SUBJECT_ICONS }
    for (const module of registeredModules()) {
      const trigger = module.workflow?.trigger
      if (!trigger) continue
      for (const family of trigger.families ?? []) families[family] = trigger.block
      for (const prefix of trigger.hrPrefixes ?? []) hrPrefixes.push([prefix, trigger.block])
      icons[trigger.block] = trigger.icon ?? module.icon
    }
    moduleBlocks = { families, hrPrefixes, icons }
  }
  return moduleBlocks
}

/** F-152: "Subiu de nível" / "Desceu de nível" (one event, `direction` filter) and "Atingiu N pontos". */
export const LEVEL_TRIGGERS = ['member.level_changed', 'member.points_changed']

/**
 * F-152: the level event is two choices on screen ("Subiu de nível", "Desceu de nível"), stored as one type with
 * `direction` (the API's default is `up`). The value of a trigger node's choice (`member.level_changed:down`)
 * and the label key of a choice (`levelTriggers.down`; a plain event keeps `triggers.<type>`).
 */
export const triggerChoiceOf = (type: string, filter?: { direction?: string } | null): string =>
  type === 'member.level_changed' ? `member.level_changed:${filter?.direction === 'down' ? 'down' : 'up'}` : type
export const triggerChoiceLabelKey = (choice: string): string =>
  choice.startsWith('member.level_changed:') ? `levelTriggers.${choice.split(':')[1]}` : `triggers.${choice}`

/** The block an event belongs to (`member.joined` → `members`). */
export const triggerBlockOf = (x: { type: string; family: string; time?: boolean }): string => {
  if (x.type === 'manual') return 'manual'
  if (x.type === 'webhook.received') return 'webhook'
  if (x.type === 'schedule') return 'time'
  /* F-152: the level and points events are their own block ("Níveis e pontos"), not "Membros". */
  if (LEVEL_TRIGGERS.includes(x.type)) return 'levels'
  if (x.family === 'hr') return blocks().hrPrefixes.find(([prefix]) => x.type.startsWith(prefix))?.[1] ?? 'hr'
  if (x.time) return 'time'
  return blocks().families[x.family] ?? x.family
}

/**
 * A readable stand-in for a catalog code the locales do not know yet
 * (`networking.session_booked` → "Session booked", `networking` → "Networking"),
 * so a new event never shows its raw key while its translation is missing.
 */
export const readableCode = (code: string | undefined): string => {
  const last = (code ?? '').split('.').pop() ?? ''
  const words = last.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : (code ?? '')
}

export const triggerBlockIcon = (block: string | undefined): typeof Zap => (block ? blocks().icons[block] : undefined) ?? Zap

/** Position on screen; unmapped families after the known blocks, in catalog order. */
export const triggerBlockRank = (block: string): number => {
  const i = (TRIGGER_BLOCKS as readonly string[]).indexOf(block)
  return i < 0 ? TRIGGER_BLOCKS.length : i
}

/**
 * Inside a block, the events by potential use (phase 5c: each module's life cycle after what people do): the first is what the block
 * starts on ("Membros" → someone joins). Events not listed keep catalog order
 * after these (people management already comes in a sensible order).
 */
const EVENT_ORDER = [
  'member.joined', 'member.referred', 'member.welcome_completed', 'member.onboarding_completed', 'member.returned', 'member.left', 'member.tag_added', 'member.group_joined', 'member.role_changed', 'member.field_changed', 'member.tag_removed', 'member.group_left',
  'purchase.completed', 'checkout.abandoned', 'checkout.started', 'checkout.recovered', 'purchase.refunded',
  'subscription.created', 'subscription.canceled', 'subscription.past_due', 'subscription.trial_ending', 'subscription.renewed', 'subscription.ended', 'subscription.plan_changed',
  'schedule', 'time.no_access', 'time.daily',
  'event.registered', 'event.checked_in', 'event.created', 'event.rescheduled', 'event.stream_started', 'event.recording_published', 'event.ticket_assigned',
  'event.registration_canceled', 'event.waitlist_offered', 'event.updated', 'event.deleted', 'event.check_in_undone',
  'course.enrolled', 'course.completed', 'lesson.completed', 'course.published', 'lesson.published', 'quiz.passed', 'quiz.failed', 'course.left', 'course.updated',
  'form.submitted', 'form.published', 'form.closed', 'form.reopened',
  'post.created', 'comment.created', 'reaction.added', 'post.updated', 'post.moved', 'post.deleted', 'comment.deleted', 'reaction.removed',
  'download.completed', 'download.item_published', 'download.file_added', 'download.file_replaced', 'download.item_updated', 'download.item_moved',
  'download.file_removed', 'download.item_deleted', 'download.category_created',
  'space.access_granted', 'space.first_visit', 'space.created', 'space.updated', 'space.deleted',
  'upvote.topic_created', 'upvote.topic_status_changed',
  'proposal.submitted', 'proposal.decided',
  'booking.created', 'booking.canceled', 'booking.no_show', 'booking.checked_in',
  'networking.joined', 'networking.session_booked', 'networking.session_completed', 'networking.reviewed', 'networking.approved', 'networking.session_no_show',
  'networking.first_session_hosted', 'networking.first_session_attended',
  'attendance.checked_in', 'attendance.checked_out',
  'badge.awarded', 'badge.revoked',
  'member.level_changed', 'member.points_changed'
]

export const triggerEventRank = (type: string): number => {
  const i = EVENT_ORDER.indexOf(type)
  return i < 0 ? EVENT_ORDER.length : i
}
